<template>
  <div :class="$style.wrapper">
    <div v-if="bidItems.length === 0" :class="$style.empty">Заявка пуста</div>

    <div v-else>
      <div :class="$style.bidList">
        <div
          v-for="group in groupedByModel"
          :key="group.modelId"
          :class="$style.modelCard"
        >
          <!-- Информация о модели -->
          <div :class="$style.modelHeader">
            <div :class="$style.modelImage">
              <img
                v-if="group.modelImage"
                :src="group.modelImage"
                :alt="group.modelTitle"
              />
              <img v-else src="@/assets/img/logo.svg" :alt="group.modelTitle" />
            </div>
            <div :class="$style.modelInfo">
              <div :class="$style.modelTitle">{{ group.modelTitle }}</div>
              <div :class="$style.modelTags">
                <span :class="$style.tagNew">Новое</span>
                <span v-if="group.totalPairs > 0" :class="$style.tagCount">
                  {{ group.totalPairs }} {{ getPairsLabel(group.totalPairs) }}
                </span>
              </div>
              <div :class="$style.priceSection">
                <div :class="$style.priceMain">
                  {{ formatPrice(group.minPrice) }} Р
                </div>
                <div :class="$style.priceRow">
                  <span :class="$style.priceLabel">Начальная цена</span>
                  <button
                    :class="$style.deleteBtn"
                    type="button"
                    @click="removeModel(group.modelId)"
                  >
                    Удалить
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Список продуктов этой модели -->
          <div :class="$style.productsList">
            <div
              v-for="product in group.products"
              :key="product.id"
              :class="$style.productItem"
            >
              <div :class="$style.productContainer">
                <div :class="$style.productRow">
                  <button :class="$style.sizeBtn" type="button">
                    {{ product.size }} {{ product.sizeType }}
                  </button>
                  <button :class="$style.priceBtn" type="button">
                    {{ formatPrice(product.price) }} Р
                  </button>
                  <div :class="$style.quantityControl">
                    <button
                      :class="$style.quantityBtn"
                      type="button"
                      @click="decreaseProductQuantity(product.id)"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                    <span :class="$style.quantityValue">
                      {{ product.quantity || 1 }}
                    </span>
                    <button
                      :class="[
                        $style.quantityBtn,
                        product.maxCount !== undefined &&
                          product.maxCount !== null &&
                          (product.quantity || 1) >= product.maxCount &&
                          $style.quantityBtnDisabled,
                      ]"
                      type="button"
                      @click="increaseProductQuantity(product.id)"
                      :disabled="
                        product.maxCount !== undefined &&
                        product.maxCount !== null &&
                        (product.quantity || 1) >= product.maxCount
                      "
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Дополнительные поля только для продуктов с isTrade -->
                <div v-if="product.isTrade" :class="$style.additionalFields">
                  <div :class="$style.field">Возможен торг</div>
                  <div :class="$style.field">
                    <input
                      :value="product.customPrice || ''"
                      type="number"
                      :class="$style.customPriceInput"
                      placeholder="Ваша цена"
                      @input="
                        updateProductCustomPrice(
                          product.id,
                          $event.target.value
                        )
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Кнопка "Добавить размер" под каждой моделью -->
          <div :class="$style.actionButtons">
            <button
              :class="[
                $style.addSizeBtn,
                isSinglePairGroup(group) && $style.addSizeBtnDisabled,
              ]"
              type="button"
              :disabled="isSinglePairGroup(group)"
              @click="
                goToSelectSize(group.modelId, group.products[0]?.sellerId)
              "
            >
              Добавить размер +
            </button>
          </div>
        </div>
      </div>

      <!-- Кнопка "Добавить модель" под всеми моделями -->
      <div :class="$style.actionButtons">
        <button @click="goToSeller" :class="$style.addModelBtn" type="button">
          Добавить модель +
        </button>
      </div>

      <!-- Итого -->
      <div :class="$style.total">
        <div :class="$style.totalLabel">Итого:</div>
        <div :class="$style.totalPrice">{{ formatPrice(totalPrice) }} ₽</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Bid",
  data() {
    return {
      mainButtonInstance: null,
      mbHandler: null,
    }
  },
  computed: {
    bidItems() {
      return this.$store.state.bid || []
    },
    groupedByModel() {
      // Группируем продукты по modelId
      const groups = {}
      this.bidItems.forEach((item) => {
        const modelId = item.modelId
        if (!groups[modelId]) {
          // Используем total_count и min_price с сервера, если они есть
          const serverTotalCount =
            item.total_count !== undefined && item.total_count !== null
              ? item.total_count
              : null
          const serverMinPrice =
            item.min_price !== undefined && item.min_price !== null
              ? item.min_price
              : null

          groups[modelId] = {
            modelId,
            modelTitle: item.modelTitle,
            modelImage: item.preview_image,
            products: [],
            // Используем данные с сервера, если есть, иначе будем вычислять
            totalPairs: serverTotalCount !== null ? serverTotalCount : 0,
            minPrice: serverMinPrice !== null ? serverMinPrice : null,
            hasServerData: serverTotalCount !== null || serverMinPrice !== null,
          }
        }
        groups[modelId].products.push(item)
        // Если нет данных с сервера, вычисляем totalPairs и minPrice
        if (!groups[modelId].hasServerData) {
          groups[modelId].totalPairs += item.quantity || 1
          if (
            !groups[modelId].minPrice ||
            item.price < groups[modelId].minPrice
          ) {
            groups[modelId].minPrice = item.price
          }
        }
      })
      return Object.values(groups)
    },
    totalPrice() {
      return this.bidItems.reduce((sum, item) => {
        const price = item.customPrice || item.price
        return sum + price * (item.quantity || 1)
      }, 0)
    },
    hasItems() {
      return this.bidItems.length > 0
    },
  },
  mounted() {
    this.setupMainButton()
  },
  beforeUnmount() {
    this.destroyMainButton()
  },
  watch: {
    hasItems() {
      this.updateMainButton()
    },
    "$store.state.bid": {
      handler() {
        this.updateMainButton()
      },
      deep: true,
    },
  },
  methods: {
    formatPrice(value) {
      if (value === null || value === undefined) return ""
      return Number(value).toLocaleString("ru-RU")
    },
    removeItem(productId) {
      this.$store.commit("removeFromBid", productId)
    },
    removeModel(modelId) {
      // Удаляем все продукты этой модели
      const productsToRemove = this.bidItems.filter(
        (item) => item.modelId === modelId
      )
      productsToRemove.forEach((item) => {
        this.$store.commit("removeFromBid", item.id)
      })
    },
    increaseProductQuantity(productId) {
      const item = this.bidItems.find((i) => i.id === productId)
      if (item) {
        const currentQuantity = item.quantity || 1
        const maxCount = item.maxCount

        // Если есть ограничение по максимальному количеству, проверяем его
        if (maxCount !== undefined && maxCount !== null) {
          if (currentQuantity >= maxCount) {
            // Уже достигнут максимум
            return
          }
        }

        this.$store.commit("updateBidQuantity", {
          productId,
          quantity: currentQuantity + 1,
        })
      }
    },
    decreaseProductQuantity(productId) {
      const item = this.bidItems.find((i) => i.id === productId)
      if (item && item.quantity > 1) {
        this.$store.commit("updateBidQuantity", {
          productId,
          quantity: item.quantity - 1,
        })
      } else if (item && item.quantity === 1) {
        this.$store.commit("removeFromBid", productId)
      }
    },
    updateProductCustomPrice(productId, value) {
      const price = value ? Number(value) : null
      this.$store.commit("updateProductCustomPrice", {
        productId,
        customPrice: price,
      })
    },
    // У селлера всего 1 пара по модели (один размер, 1 шт) — «Добавить
    // размер» неактивна: больше добавлять нечего.
    isSinglePairGroup(group) {
      return (
        group.products.length === 1 &&
        Number(group.products[0].maxCount ?? 1) === 1
      )
    },
    getPairsLabel(count) {
      const lastDigit = count % 10
      const lastTwoDigits = count % 100

      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return "пар"
      }
      if (lastDigit === 1) {
        return "пара"
      }
      if (lastDigit >= 2 && lastDigit <= 4) {
        return "пары"
      }
      return "пар"
    },
    goToSeller() {
      if (!this.bidItems.length) return

      // Получаем seller_id из первого продукта (все продукты одного продавца)
      const sellerId = this.bidItems[0]?.sellerId
      if (!sellerId) {
        window.alert("Ошибка: не указан продавец")
        return
      }

      this.$router.push({
        name: "Seller",
        params: { id: sellerId },
      })
    },
    goToSelectSize(modelId, sellerId) {
      if (!modelId) {
        window.alert("Ошибка: не указана модель")
        return
      }
      if (!sellerId) {
        window.alert("Ошибка: не указан продавец")
        return
      }

      this.$router.push({
        name: "SelectSize",
        params: { modelId },
        query: { sellerId },
      })
    },
    setupMainButton() {
      const tg = window.Telegram?.WebApp
      const mb = tg?.MainButton
      if (!mb) return

      this.mainButtonInstance = mb
      this.updateMainButton()
    },
    updateMainButton() {
      const mb = this.mainButtonInstance
      if (!mb) return

      if (this.hasItems) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        this.mbHandler = this.handleSendOffer

        mb.setText("Создать заявку")
        mb.color = "#f07818"
        mb.textColor = "#ffffff"
        mb.onClick(this.mbHandler)
        mb.show()
      } else {
        mb.hide()
      }
    },
    destroyMainButton() {
      const mb = this.mainButtonInstance
      if (mb) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        mb.hide()
      }
      this.mainButtonInstance = null
      this.mbHandler = null
    },
    async handleSendOffer() {
      if (!this.bidItems.length) return

      // Получаем seller_id из первого продукта (все продукты одного продавца)
      const sellerId = this.bidItems[0]?.sellerId
      if (!sellerId) {
        window.alert("Ошибка: не указан продавец")
        return
      }

      // Формируем items из продуктов заявки
      // Новый формат: только product_id, count и trade_price (если есть торг)
      const items = this.bidItems.map((item) => {
        const itemData = {
          product_id: Number(item.id),
          count: Number(item.quantity || 1),
        }

        // Добавляем trade_price только если isTrade === true и указана customPrice
        if (item.isTrade && item.customPrice) {
          itemData.trade_price = Number(item.customPrice)
        }

        return itemData
      })

      const payload = {
        seller_id: sellerId,
        items,
      }

      try {
        await this.$store.dispatch("sendOffer", payload)

        const tg = window.Telegram?.WebApp
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Успешно",
            message: "Заявка успешно отправлена",
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert("Заявка успешно отправлена")
        }

        // Очищаем заявку после успешной отправки
        this.$store.commit("clearBid")

        // Возвращаемся назад
        this.$router.go(-1)
      } catch (error) {
        console.error("Ошибка при отправке заявки:", error)
        const tg = window.Telegram?.WebApp
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Ошибка",
            message:
              error.response?.data?.message ||
              error.message ||
              "Не удалось отправить заявку",
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert(
            error.response?.data?.message ||
              error.message ||
              "Не удалось отправить заявку"
          )
        }
      }
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 1rem 1rem 6.5rem 1rem;
}

.empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #7b7b81;
  font-size: 1rem;
}

.bidList {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
}

.modelCard {
  /* Card container for model group */
  display: block;
}

/* Заголовок модели */
.modelHeader {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.modelImage {
  width: 7.5rem;
  height: 7.5rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform: scale(-1, 1);
  }
}

.modelInfo {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modelTitle {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: #000;
}

.modelTags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tagNew {
  background: #ec66081a;
  color: #ec6608;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
}

.tagCount {
  background: #0000000d;
  color: #00000040;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
}

.priceSection {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.priceMain {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
}

.priceRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.priceLabel {
  font-size: 0.625rem;
  color: #7b7b81;
}

.deleteBtn {
  background: transparent;
  border: none;
  color: #ff3b30;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
}

/* Список продуктов */
.productsList {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin: 0.625rem 0;
}

.productItem {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.productContainer {
  background: #f5f7fa;
  border-radius: 1rem;
  padding: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.productRow {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.sizeBtn {
  flex: 1;
  background: #fff;
  padding: 0.75rem;
  font-weight: 500;
  color: #000;
  cursor: pointer;
  transition: background 0.2s;
  border-radius: 0.75rem;
  font-size: 1rem;
  text-align: center;
  border: none;
}

.priceBtn {
  flex: 1;
  background: #fff;
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-size: 1rem;
  text-align: center;
  font-weight: 500;
  color: #000;
  cursor: pointer;
  transition: background 0.2s;
  border: none;
}

.quantityControl {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  border-radius: 0.75rem;
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  min-width: 6rem;
  justify-content: center;
}

.quantityBtn {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #f07818;
  cursor: pointer;
  padding: 0;
  border-radius: 0.25rem;
  transition: background 0.2s;

  &:hover:not(:disabled):not(.quantityBtnDisabled) {
    background: #f5f7fa;
  }

  &:disabled,
  &.quantityBtnDisabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
}

.quantityValue {
  font-size: 1rem;
  font-weight: 500;
  color: #000;
  min-width: 1.5rem;
  text-align: center;
}

/* Дополнительные поля */
.additionalFields {
  display: grid;
  grid-template-columns: auto 7.5rem;
  width: 100%;
  gap: 0.375rem;
}

.field {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.75rem;
  font-size: 1rem;
  color: #7b7b81;
  display: flex;
  align-items: center;
  justify-content: center;
  &:first-child {
    text-align: center;
    background: transparent;
  }
}

.customPriceInput {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: #000;
  text-align: left;

  &::placeholder {
    color: #7b7b81;
  }

  /* Убираем стрелки для input type="number" */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Для Firefox */
  -moz-appearance: textfield;
}

/* Кнопки действий */
.actionButtons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.625rem;
}

.addSizeBtn {
  width: 100%;
  background: #ec66080f;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: #f07818;
  cursor: pointer;
  transition: background 0.2s;
  border: none;

  &:hover {
    background: #f078181a;
  }
}

/* У селлера всего 1 пара — добавлять размер неоткуда */
.addSizeBtnDisabled {
  background: #f5f5f6;
  color: #b8b8bc;
  cursor: not-allowed;

  &:hover {
    background: #f5f5f6;
  }
}

.addModelBtn {
  width: 100%;
  background: #000;
  border: none;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }
}

/* Итого */
.total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
}

.totalLabel,
.totalPrice {
  font-size: 1.25rem;
  font-weight: 700;
  color: #000;
}
</style>
